import type {
  LegacyReportDraftData,
  LegacyReportReviewState,
} from "../types/legacyReportReview";

export function createLegacyReportDraftValues(
  draft: NonNullable<LegacyReportReviewState["draft"]>
): LegacyReportDraftData {
  return {
    ...draft.data,
    warningResolutions: draft.manifest.warnings.map((warning) => ({
      warningId: warning.id,
      note:
        draft.data.warningResolutions.find(
          (resolution) => resolution.warningId === warning.id
        )?.note || "",
    })),
  };
}

export function serializeLegacyReportDraft(
  values: LegacyReportDraftData
): LegacyReportDraftData {
  return {
    ...values,
    warningResolutions: values.warningResolutions.filter(
      (resolution) => resolution.note.trim().length > 0
    ),
  };
}

export interface LegacyRenderCompletion {
  fingerprint: string;
  pageCount: number;
}

export function legacyReportApprovalBlockers(
  state: LegacyReportReviewState,
  dirty: boolean,
  rendered: LegacyRenderCompletion | null
): string[] {
  const draft = state.draft;
  if (!draft) return ["Prepare the historical reconstruction first."];
  const blockers: string[] = [];
  if (state.baseline)
    blockers.push(
      "This historical baseline is already approved and immutable."
    );
  if (!state.capabilities.canApprove)
    blockers.push("Approval requires an authorized clinical reviewer.");
  if (dirty)
    blockers.push("Save your draft changes before reviewing the candidate.");
  if (
    !rendered ||
    rendered.fingerprint !== draft.contentFingerprint ||
    rendered.pageCount < 1
  ) {
    blockers.push(
      "Wait for the complete candidate report and all its graphics to render."
    );
  }
  if (
    !state.latestPreview ||
    state.latestPreview.revision !== draft.revision ||
    state.latestPreview.contentFingerprint !== draft.contentFingerprint
  ) {
    blockers.push("Generate a preview of this saved revision before approval.");
  }
  if (
    !state.latestComparison ||
    state.latestComparison.previewId !== state.latestPreview?.id ||
    state.latestComparison.result.contentFingerprint !==
      draft.contentFingerprint ||
    state.latestComparison.result.candidateSha256 !==
      state.latestPreview?.candidateSha256 ||
    state.latestComparison.result.sourceSha256 !== state.source.sha256
  ) {
    blockers.push("Compare the original with this revision's rendered report.");
  }
  if (
    state.latestComparison?.result.discrepancies.some(
      (finding) =>
        finding.severity === "critical" || finding.severity === "major"
    )
  ) {
    blockers.push(
      "Resolve critical and major comparison findings, save, and compare again."
    );
  }
  draft.issues
    .filter((issue) => issue.blocking)
    .forEach((issue) => blockers.push(issue.message));
  return Array.from(new Set(blockers));
}

export function canRetainLegacyReportEdits(
  current: LegacyReportReviewState,
  latest: LegacyReportReviewState
): boolean {
  return Boolean(
    !latest.baseline &&
      current.source.sha256 === latest.source.sha256 &&
      current.caseId === latest.caseId &&
      current.draft?.data.sections.length ===
        latest.draft?.data.sections.length &&
      current.draft?.data.sections.every(
        (section, index) => section.id === latest.draft?.data.sections[index].id
      )
  );
}
